import { useEffect, useState } from "react";
import { Loader2, Sparkles, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

/**
 * Panel INFORMATIVO de IVA sobre intereses por crédito (feb-2026).
 *
 * El IVA se sella al contratar el crédito y proviene del régimen fiscal SAT del cliente
 * (Art. 15-X-b LIVA). Este panel solo lo muestra; no se puede cambiar aquí.
 *
 * Reglas:
 *  - El régimen global de la entidad y el flag del crédito son de solo lectura.
 *  - Si el crédito no tiene aún el flag sellado (créditos legacy pre-CFDI Fase A),
 *    se muestra la sugerencia del motor como referencia (sin acción).
 *  - Para corregir el IVA de créditos futuros, actualiza el régimen fiscal SAT del
 *    cliente en su expediente; solo aplica a créditos nuevos.
 */
export function PanelConfirmacionIVA({ creditoId }) {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/sofom/cfdi/iva/sugerir`, { params: { credito_id: creditoId } });
        if (vivo) setDatos(data);
      } catch (e) {
        // Silencioso: si falta permiso o config, no rompe el flujo de desembolso.
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [creditoId]);

  if (loading) return (
    <div className="p-3 text-xs text-slate-500">
      <Loader2 className="h-3 w-3 inline animate-spin mr-1" />Cargando IVA…
    </div>
  );
  if (!datos) return null;

  const global = datos.iva_intereses_regimen_global;
  const globalPisa = global === "exento";
  const actual = datos.iva_intereses_credito_actual;
  const sug = datos.resolucion?.sugerencia;
  const resuelto = actual || datos.resolucion?.regimen;
  const esGravado = resuelto === "gravado";
  const etiqueta = resuelto
    ? (esGravado ? "Gravado 16 %" : "Exento (Art. 15-X-b LIVA)")
    : "—";

  return (
    <div data-testid="panel-confirmacion-iva"
         className="rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 mt-0.5 text-sky-700 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-sky-900">
              IVA sobre intereses (Art. 15-X-b LIVA)
            </p>
            <Badge variant="outline" className="text-[10px]">
              Global: {global || "gravado"}
            </Badge>
            {datos.regimen_fiscal_sat && (
              <Badge variant="outline" className="text-[10px] bg-slate-100"
                     data-testid="panel-iva-badge-regimen">
                Régimen: {datos.regimen_fiscal_sat}
              </Badge>
            )}
          </div>

          <div className="rounded-md border border-slate-300 bg-white px-3 py-2 flex items-center gap-2"
               data-testid="panel-iva-readonly">
            <Lock className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                IVA de intereses sellado al contratar
              </p>
              <p className={`text-sm font-semibold ${esGravado ? "text-emerald-700" : "text-slate-700"}`}
                 data-testid="panel-iva-valor">
                {etiqueta}
              </p>
            </div>
            <Badge variant="outline" className="text-[10px]">Solo lectura</Badge>
          </div>

          {sug && (
            <p className="text-[11px] text-slate-600">
              <strong>Criterio de sellado:</strong>{" "}
              <em data-testid="panel-iva-criterio">{sug.criterio}</em>
            </p>
          )}

          {globalPisa && (
            <p className="text-[11px] text-slate-600">
              La configuración global de la entidad es <strong>exento</strong>. Todos
              los créditos heredan exento; no hay override por crédito.
            </p>
          )}

          <div className="rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-700">
            El IVA se determina al contratar y se deriva del régimen fiscal SAT del
            cliente. Para corregirlo, actualiza el régimen del cliente en su expediente;
            solo aplica a créditos futuros. La clasificación final es responsabilidad
            fiscal de la entidad.
          </div>
        </div>
      </div>
    </div>
  );
}
